import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { anchoredOffCentre, check, repeatsAClass, valuesOf } from '../skillUsage';

export const glyphDropAndLandSkill: CodeoSkill = {
    name: SKILL_NAMES.glyphDropAndLand,
    kind: 'codeo',
    description:
        'Drop a word onto its baseline one letter at a time — each glyph falls from above the ' +
        'frame, flattens on impact and springs back to shape — for a word about falling, ' +
        'landing, crashing, squashing or weight.',
    checkLooksFor:
        'per-letter spans falling from far above (y or yPercent), then squashed on scaleX and scaleY about their baseline',
    documentUsesSkill: check(
        (script, markup, html) =>
            repeatsAClass(markup, 'span') &&
            /scaleX/.test(script) &&
            /scaleY/.test(script) &&
            ([...valuesOf(script, 'y'), ...valuesOf(script, 'yPercent').map((v) => v * 3)].some(
                (value) => value <= -300,
            ) ||
                /\by:\s*-[A-Z_(]/.test(script)) &&
            anchoredOffCentre(html),
    ),
    reviewNote:
        'A letter drawn much wider and flatter than the font, sitting on the baseline, is this ' +
        'technique working: it is the moment of impact.',
    instructions: `# Glyph Drop & Land

The letters fall. Each one drops from above the frame, accelerating, **hits the baseline, flattens** (wide and short, like a soft thing hitting a floor), **then springs back** through a couple of wobbles to its true shape. They land one after another, left to right, about two frames apart, so the word thuds into place letter by letter.

Use it on a word about falling or impact: drop, fall, land, crash, squash, heavy, sink, hit. It is \`${SKILL_NAMES.squashAndStretch}\` applied to type, so its rules for the floor and the contact edge hold here too.

${KINETIC_WORD_SETTING}

## Markup

Capitals, sized to the frame by font-size (\`data-l5-allow-literal\`), every letter an \`inline-block\` span scaled about its baseline. Put the word low in the frame: there has to be a long way to fall.

\`\`\`html
<div id="drop-beat" class="absolute inset-0 flex items-end justify-center" style="padding-bottom: 12%;">
  <div id="drop-word" data-l5-allow-literal style="line-height: 1; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase; color: var(--l5-color-3, #fafafa);">
    <span class="dl-ch">D</span><span class="dl-ch">R</span><span class="dl-ch">O</span><span class="dl-ch">P</span>
  </div>
</div>
\`\`\`

\`\`\`css
.dl-ch { display: inline-block; transform-origin: 50% 88%; }
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const FRAME_H = 1080;
const word = document.querySelector('#drop-word');
fitWord(word, 0.84);

const CUE = 0.45;            // the first letter lands on the word
const FALL = 0.13;           // fast: a letter falls the frame in about 8 frames
const GAP = 0.033;           // two frames between landings
const letters = [...word.querySelectorAll('.dl-ch')];

letters.forEach((ch, i) => {
  const land = CUE + i * GAP;
  // From above the frame, accelerating, a little tall while it falls.
  tl.fromTo(ch,
    { y: -FRAME_H, scaleX: 0.88, scaleY: 1.18 },
    { y: 0, duration: FALL, ease: 'power2.in' },
    land - FALL);
  // Flattened the instant it lands...
  tl.set(ch, { scaleX: 1.45, scaleY: 0.5 }, land);
  // ...then springing back through its wobbles to its true shape.
  tl.to(ch, { scaleX: 1, scaleY: 1, duration: 0.42, ease: 'elastic.out(1.1, 0.4)' }, land + 0.017);
});

tl.set({}, {}, 1.8);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Heavier.** A heavy word lands harder and settles sooner: squash to about 1.25 × 0.7, and settle with \`back.out(1.2)\` in 0.2s instead of the elastic.
- **The frame feels it.** On the last letter's landing, kick the whole line (or the frame) down 8–12px for two frames and back.
- **Into a mask.** For a word that falls *into* view and away, combine with \`${SKILL_NAMES.letterMaskRise}\` from above instead.

## Pitfalls

- **Off the frame before it falls.** A letter visible in the air before it moves hangs there. Start each one at least a frame-height above its landing.
- **Squash only on the floor.** The flat pose is set on the frame of contact, never earlier, and the origin is the baseline so the flattened letter stays on the line.
- **Never slow the fall to fill time.** The fall is gravity; hold the landed word instead.
- The letters are transform-tweened, so they carry no CSS \`transform\` beyond the origin.`,
};
